import { useEffect, useMemo, useRef, useState } from "react"
import toast from "react-hot-toast"
import { useHotkeys } from "react-hotkeys-hook"

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  Badge,
  Button,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@aix/ui"

import { useDebouncedValue } from "../lib/filters/useUrlFilters"
import { trpc } from "../lib/trpc"
import { keywordStatusBadgeVariant, SEO_KEYWORD_STATUSES } from "./seoBoardHelpers"

const ANY = "__any__"
const PAGE_SIZE = 100

interface SEOKeywordsTabProps {
  productKey: string
}

export default function SEOKeywordsTab({ productKey }: SEOKeywordsTabProps) {
  const [statusFilter, setStatusFilter] = useState<string>(ANY)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [activeRowId, setActiveRowId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState("")
  const [pageOffset, setPageOffset] = useState(0)
  const [pendingStatus, setPendingStatus] = useState<string | null>(null)
  const [pendingPageEdge, setPendingPageEdge] = useState<"first" | "last" | null>(null)
  const searchRef = useRef<HTMLInputElement>(null)
  const selectedRowRef = useRef<HTMLTableRowElement>(null)

  const utils = trpc.useUtils()
  const debouncedSearchQuery = useDebouncedValue(searchQuery.trim(), 250)
  const listInput = {
    productKey,
    status: statusFilter === ANY ? undefined : statusFilter,
    query: debouncedSearchQuery || undefined,
    limit: PAGE_SIZE,
    offset: pageOffset,
  }
  const { data, isLoading, error } = trpc.seo.keywords.list.useQuery(listInput)
  const rows = useMemo(() => data?.rows ?? [], [data?.rows])

  const activeIndex = rows.findIndex((row) => row.id === activeRowId)
  useEffect(() => {
    if (isLoading || !rows.length) return
    if (pendingPageEdge) {
      setActiveRowId(pendingPageEdge === "first" ? rows[0].id : (rows.at(-1)?.id ?? null))
      setPendingPageEdge(null)
      return
    }
    if (activeIndex < 0) setActiveRowId(rows[0].id)
  }, [activeIndex, isLoading, pendingPageEdge, rows])
  useEffect(() => selectedRowRef.current?.scrollIntoView({ block: "nearest" }), [activeRowId])
  useEffect(() => {
    setPendingPageEdge(null)
    setPageOffset(0)
  }, [debouncedSearchQuery, productKey, statusFilter])

  const setStatusMutation = trpc.seo.keywords.setStatus.useMutation({
    onError: (e) => toast.error(e.message || "Update failed"),
    onSuccess: (res) => {
      toast.success(`Updated ${res.updated} keyword(s)`)
      setSelectedIds(new Set())
      void utils.seo.keywords.list.invalidate()
    },
  })

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const allSelected = rows.length > 0 && rows.every((r) => selectedIds.has(r.id))
  const toggleAll = () => {
    if (allSelected) setSelectedIds(new Set())
    else setSelectedIds(new Set(rows.map((r) => r.id)))
  }

  const applyStatus = (status: string) => {
    if (selectedIds.size === 0) return
    setStatusMutation.mutate({ productKey, ids: [...selectedIds], status })
  }

  const moveSelection = (direction: 1 | -1) => {
    if (!rows.length) return
    if (direction === 1 && activeIndex === rows.length - 1 && data?.hasMore) {
      setPendingPageEdge("first")
      setPageOffset(data.nextOffset ?? pageOffset + PAGE_SIZE)
      return
    }
    if (direction === -1 && activeIndex <= 0 && pageOffset > 0) {
      setPendingPageEdge("last")
      setPageOffset(Math.max(0, pageOffset - PAGE_SIZE))
      return
    }
    const nextIndex = activeIndex < 0 ? 0 : Math.max(0, Math.min(rows.length - 1, activeIndex + direction))
    setActiveRowId(rows[nextIndex].id)
  }
  useHotkeys("j, down", () => moveSelection(1), { preventDefault: true, enableOnFormTags: false }, [
    activeIndex,
    data?.hasMore,
    data?.nextOffset,
    pageOffset,
    rows,
  ])
  useHotkeys("k, up", () => moveSelection(-1), { preventDefault: true, enableOnFormTags: false }, [
    activeIndex,
    data?.hasMore,
    data?.nextOffset,
    pageOffset,
    rows,
  ])
  useHotkeys("g", () => rows[0] && setActiveRowId(rows[0].id), { preventDefault: true, enableOnFormTags: false }, [
    rows,
  ])
  useHotkeys(
    "shift+g",
    () => rows.at(-1) && setActiveRowId(rows.at(-1)?.id ?? null),
    { preventDefault: true, enableOnFormTags: false },
    [rows],
  )
  useHotkeys(
    "enter, space",
    () => activeRowId && toggle(activeRowId),
    { preventDefault: true, enableOnFormTags: false },
    [activeRowId],
  )
  useHotkeys("/", () => searchRef.current?.focus(), { preventDefault: true, enableOnFormTags: false })
  useHotkeys(
    "esc",
    () => {
      if (document.activeElement === searchRef.current) searchRef.current?.blur()
    },
    { enableOnFormTags: true },
  )
  useHotkeys(
    "backslash",
    () => {
      setSearchQuery("")
      setStatusFilter(ANY)
      setPageOffset(0)
    },
    { preventDefault: true, enableOnFormTags: false },
  )

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b-2 border-border bg-background px-3 py-2">
        <Input
          ref={searchRef}
          value={searchQuery}
          onChange={(event) => setSearchQuery(event.target.value)}
          aria-label="Search SEO keywords or page paths"
          placeholder="Search keywords or paths… ( / )"
          className="h-8 w-56 text-xs"
        />
        <span className="sr-only" aria-live="polite">
          {activeIndex >= 0 ? `Active keyword: ${rows[activeIndex]?.keyword ?? "unknown"}` : "No active keyword"}
        </span>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="h-8 w-40 text-xs">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ANY}>Status: any</SelectItem>
            {SEO_KEYWORD_STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-muted-foreground">{selectedIds.size} selected</span>
          <Button
            size="sm"
            onClick={() => setPendingStatus("approved")}
            disabled={selectedIds.size === 0}
            loading={setStatusMutation.isLoading}
          >
            Approve
          </Button>
          <Button
            size="sm"
            variant="destructive"
            onClick={() => setPendingStatus("rejected")}
            disabled={selectedIds.size === 0}
          >
            Reject
          </Button>
        </div>
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        {error ? (
          <div className="m-4 rounded border-2 border-destructive bg-destructive/10 p-4 text-sm text-destructive">
            {error.message}
          </div>
        ) : isLoading ? (
          <div className="p-8 text-center text-muted-foreground">
            <div className="mb-2 inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
            <div>Loading…</div>
          </div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-muted-foreground">No keywords in this queue.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-secondary text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2">
                  <Checkbox checked={allSelected} onCheckedChange={toggleAll} aria-label="Select all" />
                </th>
                <th className="px-3 py-2">Keyword</th>
                <th className="px-3 py-2">Slug</th>
                <th className="px-3 py-2">Cluster</th>
                <th className="px-3 py-2">Family</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Vol</th>
                <th className="px-3 py-2">Diff</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr
                  ref={activeRowId === r.id ? selectedRowRef : undefined}
                  key={r.id}
                  onClick={() => setActiveRowId(r.id)}
                  className={`border-b border-border hover:bg-secondary ${activeRowId === r.id ? "bg-secondary" : ""}`}
                >
                  <td className="px-3 py-2">
                    <Checkbox
                      checked={selectedIds.has(r.id)}
                      onCheckedChange={() => toggle(r.id)}
                      aria-label={`Select ${r.keyword}`}
                    />
                  </td>
                  <td className="px-3 py-2">{r.keyword}</td>
                  <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{r.slug}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.cluster ?? "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.family ?? "—"}</td>
                  <td className="px-3 py-2">
                    <Badge variant={keywordStatusBadgeVariant(r.status)}>{r.status}</Badge>
                  </td>
                  <td className="px-3 py-2 text-muted-foreground">{r.volume ?? "—"}</td>
                  <td className="px-3 py-2 text-muted-foreground">{r.difficulty ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <div className="flex items-center justify-between gap-2 border-t border-border p-2">
        <Button
          size="sm"
          variant="outline"
          disabled={pageOffset === 0}
          onClick={() => setPageOffset(Math.max(0, pageOffset - PAGE_SIZE))}
        >
          Previous
        </Button>
        <span className="text-[11px] text-muted-foreground">
          {rows.length ? `${pageOffset + 1}–${pageOffset + rows.length}` : "0"} keyword(s)
        </span>
        <Button
          size="sm"
          variant="outline"
          disabled={!data?.hasMore}
          onClick={() => setPageOffset(data?.nextOffset ?? pageOffset)}
        >
          Next
        </Button>
      </div>

      <AlertDialog open={Boolean(pendingStatus)} onOpenChange={(open) => !open && setPendingStatus(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingStatus === "approved" ? "Approve" : "Reject"} {selectedIds.size} keyword(s)?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This changes keyword planning state only. It does not create, edit, compile, or publish pages.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (pendingStatus) applyStatus(pendingStatus)
                setPendingStatus(null)
              }}
            >
              Confirm status change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
